/**
 * 3-tier cache with TTL support.
 * L1: In-memory Map (fastest, per-session)
 * L2: IndexedDB (persistent, web primary)
 * L3: localStorage (fallback when IndexedDB unavailable)
 *
 * Reads check L1 first, then L2, then L3 (promoting to higher tiers on hit).
 * Writes propagate to all available tiers.
 * Each tier enforces TTL. Size limits prevent unbounded growth.
 */

import { DEFAULTS } from '@/config/defaults';

// ── Serializable cache entry format (shared across tiers) ──

interface SerializedEntry {
  /** JSON-serialized value. */
  v: string;
  /** Timestamp when entry was written (ms since epoch). */
  t: number;
  /** TTL in ms. */
  ttl: number;
}

function isExpired(entry: SerializedEntry): boolean {
  return Date.now() - entry.t > entry.ttl;
}

// ── L1: In-memory tier ──

const L1_MAX_ENTRIES = 2000;

class MemoryTier {
  private store = new Map<string, SerializedEntry>();

  get(key: string): SerializedEntry | undefined {
    const entry = this.store.get(key);
    if (!entry) return undefined;
    if (isExpired(entry)) {
      this.store.delete(key);
      return undefined;
    }
    return entry;
  }

  getStale(key: string): SerializedEntry | undefined {
    return this.store.get(key);
  }

  set(key: string, entry: SerializedEntry): void {
    if (this.store.size >= L1_MAX_ENTRIES && !this.store.has(key)) {
      const firstKey = this.store.keys().next().value;
      if (firstKey !== undefined) this.store.delete(firstKey);
    }
    this.store.set(key, entry);
  }

  delete(key: string): void { this.store.delete(key); }
  clear(): void { this.store.clear(); }

  prune(): void {
    const now = Date.now();
    for (const [key, entry] of this.store) {
      if (now - entry.t > entry.ttl) this.store.delete(key);
    }
  }
}

// ── L2: IndexedDB tier ──

const IDB_NAME = 'sentinel-cache';
const IDB_STORE = 'entries';
const IDB_VERSION = 1;
const IDB_MAX_ENTRIES = 5000;

class IndexedDBTier {
  private db: IDBDatabase | null = null;
  private opening: Promise<IDBDatabase | null> | null = null;

  private open(): Promise<IDBDatabase | null> {
    if (this.db) return Promise.resolve(this.db);
    if (this.opening) return this.opening;

    this.opening = new Promise<IDBDatabase | null>((resolve) => {
      try {
        const request = indexedDB.open(IDB_NAME, IDB_VERSION);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(IDB_STORE)) {
            db.createObjectStore(IDB_STORE);
          }
        };
        request.onsuccess = () => {
          this.db = request.result;
          resolve(this.db);
        };
        request.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
    return this.opening;
  }

  async get(key: string): Promise<SerializedEntry | undefined> {
    const db = await this.open();
    if (!db) return undefined;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(IDB_STORE, 'readonly');
        const store = tx.objectStore(IDB_STORE);
        const request = store.get(key);
        request.onsuccess = () => {
          const entry = request.result as SerializedEntry | undefined;
          if (!entry || isExpired(entry)) {
            if (entry) this.delete(key).catch(() => {});
            resolve(undefined);
          } else {
            resolve(entry);
          }
        };
        request.onerror = () => resolve(undefined);
      } catch { resolve(undefined); }
    });
  }

  async set(key: string, entry: SerializedEntry): Promise<void> {
    const db = await this.open();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).put(entry, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch { resolve(); }
    });
  }

  async delete(key: string): Promise<void> {
    const db = await this.open();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch { resolve(); }
    });
  }

  async clear(): Promise<void> {
    const db = await this.open();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch { resolve(); }
    });
  }

  async prune(): Promise<void> {
    const db = await this.open();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        const store = tx.objectStore(IDB_STORE);
        const now = Date.now();
        let count = 0;
        const cursor = store.openCursor();
        cursor.onsuccess = () => {
          const c = cursor.result;
          if (!c) { resolve(); return; }
          count++;
          const entry = c.value as SerializedEntry;
          if (now - entry.t > entry.ttl || count > IDB_MAX_ENTRIES) c.delete();
          c.continue();
        };
        cursor.onerror = () => resolve();
      } catch { resolve(); }
    });
  }
}

// ── L3: localStorage tier ──

const LS_PREFIX = 'sentinel-cache:';
const LS_MAX_ENTRIES = 500;

class LocalStorageTier {
  get(key: string): SerializedEntry | undefined {
    try {
      const raw = localStorage.getItem(LS_PREFIX + key);
      if (!raw) return undefined;
      const entry = JSON.parse(raw) as SerializedEntry;
      if (isExpired(entry)) {
        localStorage.removeItem(LS_PREFIX + key);
        return undefined;
      }
      return entry;
    } catch { return undefined; }
  }

  set(key: string, entry: SerializedEntry): void {
    try {
      localStorage.setItem(LS_PREFIX + key, JSON.stringify(entry));
    } catch {
      this.evict();
      try { localStorage.setItem(LS_PREFIX + key, JSON.stringify(entry)); } catch { /* full */ }
    }
  }

  delete(key: string): void {
    try { localStorage.removeItem(LS_PREFIX + key); } catch { /* ignore */ }
  }

  clear(): void {
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k?.startsWith(LS_PREFIX)) keys.push(k);
      }
      for (const k of keys) localStorage.removeItem(k);
    } catch { /* ignore */ }
  }

  prune(): void {
    try {
      const now = Date.now();
      const remove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k?.startsWith(LS_PREFIX)) continue;
        try {
          const e = JSON.parse(localStorage.getItem(k)!) as SerializedEntry;
          if (now - e.t > e.ttl) remove.push(k);
        } catch { remove.push(k); }
      }
      for (const k of remove) localStorage.removeItem(k);
    } catch { /* ignore */ }
  }

  private evict(): void {
    try {
      const entries: { key: string; time: number }[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k?.startsWith(LS_PREFIX)) continue;
        try {
          const e = JSON.parse(localStorage.getItem(k)!) as SerializedEntry;
          entries.push({ key: k, time: e.t });
        } catch { entries.push({ key: k, time: 0 }); }
      }
      entries.sort((a, b) => a.time - b.time);
      const toRemove = Math.max(entries.length - LS_MAX_ENTRIES + 50, 50);
      for (let i = 0; i < Math.min(toRemove, entries.length); i++) {
        localStorage.removeItem(entries[i].key);
      }
    } catch { /* ignore */ }
  }
}

// ── TieredCache (public API — same interface as old MemoryCache) ──

/**
 * 3-tier cache. L1 memory → L2 IndexedDB → L3 localStorage.
 * Synchronous `get()` checks L1 and L3 only. Use `getAsync()` for full L2 lookup.
 */
export class TieredCache {
  private l1 = new MemoryTier();
  private l2 = new IndexedDBTier();
  private l3 = new LocalStorageTier();
  private defaultTtl: number;

  constructor(defaultTtl?: number) {
    this.defaultTtl = defaultTtl ?? DEFAULTS.cache.maxAge;
    if (typeof setInterval !== 'undefined') {
      setInterval(() => this.prune(), 5 * 60 * 1000);
    }
  }

  /** Synchronous get — checks L1 (memory) and L3 (localStorage). */
  get<T>(key: string): T | undefined {
    const l1 = this.l1.get(key);
    if (l1) return JSON.parse(l1.v) as T;

    const l3 = this.l3.get(key);
    if (l3) {
      this.l1.set(key, l3); // promote to L1
      return JSON.parse(l3.v) as T;
    }
    return undefined;
  }

  /** Async get — checks all tiers including IndexedDB. */
  async getAsync<T>(key: string): Promise<T | undefined> {
    const l1 = this.l1.get(key);
    if (l1) return JSON.parse(l1.v) as T;

    const l2 = await this.l2.get(key);
    if (l2) {
      this.l1.set(key, l2);
      return JSON.parse(l2.v) as T;
    }

    const l3 = this.l3.get(key);
    if (l3) {
      this.l1.set(key, l3);
      return JSON.parse(l3.v) as T;
    }
    return undefined;
  }

  /** Get value even if expired (for stale-on-error fallback). L1 only. */
  getStale<T>(key: string): T | undefined {
    const entry = this.l1.getStale(key);
    return entry ? JSON.parse(entry.v) as T : undefined;
  }

  /** Set a value in all cache tiers. */
  set<T>(key: string, value: T, ttl?: number): void {
    const entry: SerializedEntry = {
      v: JSON.stringify(value),
      t: Date.now(),
      ttl: ttl ?? this.defaultTtl,
    };
    this.l1.set(key, entry);
    this.l2.set(key, entry).catch(() => {});
    this.l3.set(key, entry);
  }

  has(key: string): boolean { return this.get(key) !== undefined; }

  delete(key: string): boolean {
    this.l1.delete(key);
    this.l2.delete(key).catch(() => {});
    this.l3.delete(key);
    return true;
  }

  clear(): void {
    this.l1.clear();
    this.l2.clear().catch(() => {});
    this.l3.clear();
  }

  prune(): void {
    this.l1.prune();
    this.l2.prune().catch(() => {});
    this.l3.prune();
  }
}

// ── Backwards compatibility ──

/**
 * MemoryCache — kept so existing imports of `MemoryCache` class still compile.
 * The singleton `cache` export is now a TieredCache.
 */
export class MemoryCache extends TieredCache {}

/** Singleton cache instance (3-tier). */
export const cache = new TieredCache();
